import DownloadIcon from "@mui/icons-material/Download";
import MapIcon from "@mui/icons-material/Map";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadEllipseCsv } from "../../api/methods";
import { JacobBearResult } from "../../types/interfaces/methods";
import { wktPolygonToGeoJson } from "../../utils/WKT/wkt";
import CaptureEllipseMapDialog from "./CaptureEllipseMapDialog";

interface JacobBearResultsProps {
  calculations: JacobBearResult[];
  captureId?: number;
}

export default function JacobBearResults({ calculations, captureId }: Readonly<JacobBearResultsProps>) {
  const { t } = useTranslation();
  const [selectedWkt, setSelectedWkt] = useState<string | null>(null);

  const formatDecimals = (value?: number | null) =>
    typeof value === "number"
      ? value.toLocaleString(undefined, { maximumFractionDigits: 3 })
      : "-";

  const columns = (t: any): GridColDef[] => [
    { 
      field: "gradient", 
      headerName: t("calculations.methods.jacob-bear.results.gradienteHidraulico"), 
      flex: 1,
      valueFormatter: (v) => formatDecimals(v) 
    },
    { 
      field: "tCalc", 
      headerName: t("calculations.methods.jacob-bear.results.tempCalculo"), 
      flex: 0.8,
      valueFormatter: (v) => formatDecimals(v) 
    },
    { 
      field: "azimute", 
      headerName: t("calculations.methods.jacob-bear.results.azimute"), 
      flex: 0.7,
      valueFormatter: (v) => formatDecimals(v) 
    },
    { 
      field: "tResidual", 
      headerName: t("calculations.methods.jacob-bear.results.temmpoResidual"), 
      flex: 0.7, 
      valueFormatter: (v) => formatDecimals(v) 
    },
    { 
      field: "dContra", 
      headerName: t("calculations.methods.jacob-bear.results.distMaxDirecaoContraria"), 
      flex: 1, 
      valueFormatter: (v) => formatDecimals(v) 
    },
    { 
      field: "dDirecao", 
      headerName: t("calculations.methods.jacob-bear.results.distMaxNaDirecao"), 
      flex: 1, 
      valueFormatter: (v) => formatDecimals(v) 
    },
    { 
      field: "dPerpendicular", 
      headerName: t("calculations.methods.jacob-bear.results.distMaxDirecaoPerpendicular"), 
      flex: 1, 
      valueFormatter: (v) => formatDecimals(v) 
    },
    {
      field: "ellipseWkt",
      headerName: t("calculations.methods.jacob-bear.results.ellipse"),
      flex: 0.45,
      minWidth: 90,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        params.value ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Tooltip title={t("calculations.methods.jacob-bear.results.viewEllipse")}>
              <IconButton
                size="small"
                onClick={() => setSelectedWkt(params.value)}
              >
                <MapIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("calculations.methods.jacob-bear.results.downloadEllipseCsv")}>
              <IconButton
                size="small"
                onClick={() =>
                  downloadEllipseCsv(
                    captureId!,
                    params.row.ellipse,
                  )
                }
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null,
    }
  ];

  const rows = (r: any) => r.calculations.map((ug: any, i: number) => ({
    id: i,
    gradient: ug.iJB,
    tCalc: ug.tJB,
    azimute: ug.gradi,
    tResidual: ug.jbTr,
    dContra: ug.jbXmAC,
    dDirecao: ug.jbXmAB,
    dPerpendicular: ug.jbYmDE,
    ellipseWkt: ug.ellipseWkt,

    ellipse: {
      downstreamDistanceMeters: ug.jbXmAB,
      upstreamDistanceMeters: ug.jbXmAC,
      perpendicularDistanceMeters: ug.jbYmDE,
      azimuthDegrees: ug.gradi,
    },
  }));

  const selectedEllipse = selectedWkt ? wktPolygonToGeoJson(selectedWkt) : null;
  
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
        {t("calculations.methods.results-title")}
      </Typography>

      {calculations.map((calc, index) => {
        const r = calc.results;
        return (
          <Box key={index} sx={{ mb: 6 }}>
            <DataGrid
              rows={rows(r)}
              columns={columns(t).map((c) => ({ ...c, sortable: false }))}
              hideFooter
              disableColumnMenu
              sx={{
                boxShadow: 1,
                "& .MuiDataGrid-columnHeaders": { minHeight: "120px !important", maxHeight: "80px !important" },
                "& .MuiDataGrid-columnHeader": { minHeight: "120px !important", maxHeight: "80px !important", lineHeight: "20px !important" },
                "& .MuiDataGrid-columnHeaderTitle": { whiteSpace: "normal" },
              }}
            />
          </Box>
        );
      })}

      <CaptureEllipseMapDialog
        open={!!selectedWkt}
        onClose={() => setSelectedWkt(null)}
        ellipse={selectedEllipse}
        captureId={captureId}
      />
    </Box>
  );
}